import { Button, Text, makeStyles, shorthands } from '@fluentui/react-components';
import { Add16Filled, LineHorizontal116Filled } from '@fluentui/react-icons';
import { Eye } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ISource } from '../../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations } from '../../../store/hooks';
import { DocumentIcon } from '../../Common/DocumentIcon';

interface Props {
    items: ISource[];
    initialVisibleItemCount?: number;
    onSourceClick: (source: ISource) => void;
}

const useStyles = makeStyles({
    knowledgeContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '12px',
    },
    knowledgeLabel: {
        fontWeight: 600,
        fontSize: '0.75rem',
        color: 'var(--colorNeutralForeground2, #666)',
        marginBottom: '2px',
    },
    knowledgeTokens: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    knowledgeToken: {
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: 'none',
        padding: '4px 12px',
        borderRadius: '16px',
        border: '1px solid var(--colorNeutralStroke2, #ddd)',
        backgroundColor: 'var(--colorNeutralBackground3, #f4f4f4)',
        color: 'var(--colorNeutralForeground2, #666)',
        fontSize: '0.8125rem',
        fontWeight: 500,
        gap: '6px',
        cursor: 'pointer',
    },
    knowledgeIcon: {
        color: 'var(--colorNeutralForeground3, #999)',
        fontSize: '14px',
    },
    knowledgeName: {
        whiteSpace: 'nowrap',
        display: 'inline',
    },
    eyeIcon: {
        color: 'var(--colorNeutralForeground3, #999)',
        fontSize: '14px',
        marginLeft: '4px',
        flexShrink: 0,
    },
});

// renders the list of sources supporting the answer (e.g. for RAG tools)
export const AnswerSources: FC<Props> = ({ items, initialVisibleItemCount = 1, onSourceClick }) => {
    const [visibleItemCount, setVisibleItemCount] = useState(initialVisibleItemCount);
    const visibleItems = items.slice(0, visibleItemCount);
    const showMore = () => setVisibleItemCount(items.length);
    const showLess = () => setVisibleItemCount(initialVisibleItemCount);
    const { t } = useTranslation();
    const classes = useStyles();
    const { activeGpt } = useConversations();

    if (items.length === 0 || activeGpt?.isDefault) {
        return null;
    }

    return (
        <div className={classes.knowledgeContainer}>
            <span className="knowledgeLabel slate600">{t('conversation.answer.knowledge', { count: items.length })} :</span>
            <div className={classes.knowledgeTokens}>
                {visibleItems.map((source, index) => {
                    const displayName = decodeURI(source.fileName || source.title || source.webSiteUrl || `Source ${index + 1}`);
                    return (
                        <div
                            key={index}
                            className="knowledgeToken"
                            onClick={() => onSourceClick(source)}
                            title={source.webSiteUrl || source.title || ''}
                        >
                            <DocumentIcon className="knowledgeIcon" />
                            <span className="knowledgeName">{index + 1}. {displayName}</span>
                            <Eye className="eyeIcon" size={14} aria-hidden="true" />
                        </div>
                    );
                })}
            </div>
            {visibleItemCount < items.length ? (
                <Button appearance='secondary' size='small' onClick={(_: any) => showMore()} icon={<Add16Filled />}>
                    {t('conversation.answer.seeMoreSources', { hiddenCount: items.length - visibleItemCount })}
                </Button>
            ) : initialVisibleItemCount >= items.length ? null : (
                <Button appearance='secondary' size='small' onClick={(_: any) => showLess()} icon={<LineHorizontal116Filled />}>
                    {t('conversation.answer.seeLessSources')}
                </Button>
            )}
        </div>
    );
};
